import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Shield,
  Scales,
  Buildings,
  ChartBar,
} from "@phosphor-icons/react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";

const offerings = [
  {
    title: "Statutory Attest Functions",
    desc: "Specialised teams for audits under the Indian Companies Act and Income Tax Act — including statutory, tax and stock audits.",
    icon: Shield,
  },
  {
    title: "US GAAP, IFRS & Ind AS Restatements",
    desc: "Restatement support for parent reporting and group consolidation across US GAAP, IFRS and Ind AS frameworks.",
    icon: ChartBar,
  },
  {
    title: "Internal Audit",
    desc: "Compliance audits, process adherence, data analytics, systems audits and proprietary internal audit programs — a business approach that delivers value in every sphere of operation.",
    icon: Buildings,
  },
  {
    title: "Management Assurance & Risk Review",
    desc: "Corporate governance, internal control design and review, IFC (Internal Financial Controls) process design, walkthroughs and testing.",
    icon: Scales,
  },
];

const sectors = [
  "Property & Real Estate",
  "Retail",
  "Software & Technology",
  "Hospitals & Healthcare",
  "Education",
  "Investment Funds",
];

export default function AuditAssurance() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="audit-assurance-page" className="bg-[#FDFBF7] text-[#1B3B2B]">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <Link
              to="/"
              data-testid="back-to-home"
              className="inline-flex items-center gap-2 overline text-[10px] mb-8 text-[#4A5D53] hover:text-[#D4AF37] transition-colors"
            >
              <ArrowLeft size={12} weight="bold" /> Back to home
            </Link>
            <div className="overline mb-5">/ Service · Audit &amp; Assurance</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#1B3B2B]">
              Independent, unbiased opinions on the accuracy of financial statements.
            </h1>
            <p className="mt-8 max-w-2xl text-[#4A5D53] text-base sm:text-lg leading-relaxed">
              At AACO, we offer a comprehensive range of audit and assurance services. Our
              commitment to delivering independent and unbiased opinions on the accuracy and
              completeness of financial statements for a given period has been established over
              decades.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="/#contact" className="btn-primary">
                Speak to a partner <ArrowRight size={16} weight="bold" />
              </a>
              <Link to="/" className="btn-secondary">
                All services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Detail paragraphs */}
      <section className="py-20 md:py-28 bg-[#F3F0EA]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="overline mb-5">/ Approach</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl leading-tight text-[#1B3B2B] mb-10 max-w-3xl">
            Specialised teams for each attest function — domestic and cross-border.
          </h2>
          <div className="space-y-6 text-[#4A5D53] text-base leading-relaxed">
            <p>
              We understand the importance of specialised teams for statutory attest functions
              under the Indian Companies Act and Income Tax regulations. In an increasingly
              globalised world, US GAAP and IFRS restatements are becoming more critical — and our
              team has the expertise to handle both. We are also knowledgeable in Ind AS
              restatements.
            </p>
            <p>
              Our approach to internal audits combines compliance audits, process adherence, data
              analytics, systems audits and proprietary audits. We believe in a business approach
              to internal audits, and our representations in internal audit reports deliver value
              in every sphere of business.
            </p>
            <p>
              In addition to audit and assurance, we specialise in management assurance and risk
              review, corporate governance, internal control design and review, and IFC process
              and walkthroughs. The firm brings together partners with decades of experience, AI
              and data scientists for domain knowledge, and industry domain experts.
            </p>
          </div>
        </div>
      </section>

      {/* Offerings */}
      <section className="py-20 md:py-28 bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="overline mb-5">/ What We Offer</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#1B3B2B] mb-12 max-w-3xl">
            Four lines of audit and assurance practice.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {offerings.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.div
                  key={o.title}
                  data-testid={`audit-offering-${i}`}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.55, delay: i * 0.07 }}
                  className="card-flat p-8 md:p-10"
                >
                  <div className="flex items-start justify-between">
                    <Icon size={30} weight="duotone" className="text-[#1B3B2B]" />
                    <span className="overline text-[10px]">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="font-serif-display text-2xl mt-8 leading-snug text-[#1B3B2B]">
                    {o.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#4A5D53] leading-relaxed">{o.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sector expertise */}
      <section className="py-20 md:py-28 bg-[#1B3B2B] text-[#FDFBF7]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
          <div className="overline mb-5 text-[#D4AF37]">/ Sector Expertise</div>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#FDFBF7] mb-10 max-w-3xl">
            Partners, data scientists and industry domain experts.
          </h2>
          <p className="text-[#D8D2C2] leading-relaxed max-w-2xl mb-10">
            Our firm brings together partners with decades of experience, AI and data scientists
            for domain knowledge, and industry domain experts across:
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
            {sectors.map((s) => (
              <li key={s} className="flex items-start gap-3 text-[#FDFBF7]">
                <CheckCircle size={20} weight="duotone" className="text-[#D4AF37] mt-0.5 shrink-0" />
                <span className="text-sm">{s}</span>
              </li>
            ))}
          </ul>

          <div className="mt-14">
            <a href="/#contact" className="btn-secondary !border-[#D4AF37] !text-[#D4AF37] hover:!bg-[#D4AF37]/10">
              Engage AACO for an audit <ArrowRight size={16} weight="bold" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
